/**
 * What a friends-watching notice offers (D-120), in the bell list and the pop-up alike:
 * - seat-offer "A seat is free at Bea's table": Join, while the two minutes last (`until`), then nothing;
 * - friend-table "Bea started a private table": Join or Watch;
 * - seat-request "Cy asked for a seat at your table": View (your table, where the line shows).
 * Each `run` resolves to where to go, or throws the server's sentence.
 */
import { useEffect, useState } from "react";
import type { NotificationItem } from "./types";
import { joinFriendTable, watchTable } from "./watching";

/** How long a freed seat is held (the server's OFFER_MS). */
export const OFFER_MS = 2 * 60_000;
export interface NoticeAction { label: string; testId: string; run: () => Promise<string> }

const codeOf = (n: Pick<NotificationItem, "href">): string => (n.href ?? "").split("/").pop() ?? "";
export const isWatchNotice = (kind: string): boolean => kind === "seat-offer" || kind === "friend-table" || kind === "seat-request";

/** When a seat offer runs out; null for other notices. */
export const offerUntil = (n: Pick<NotificationItem, "kind" | "at">): number | null => (n.kind === "seat-offer" ? n.at + OFFER_MS : null);

export function noticeActions(n: Pick<NotificationItem, "kind" | "href" | "at">, now: number): NoticeAction[] {
  const code = codeOf(n);
  if (!code) return [];
  if (n.kind === "seat-offer") return now < n.at + OFFER_MS ? [{ label: "Join", testId: "notice-join", run: () => joinFriendTable(code) }] : [];
  if (n.kind === "friend-table") return [
    { label: "Join", testId: "notice-join", run: () => joinFriendTable(code) },
    { label: "Watch", testId: "notice-watch", run: () => watchTable(code) },
  ];
  if (n.kind === "seat-request") return [{ label: "View", testId: "notice-view", run: async () => `/table/${encodeURIComponent(code)}` }];
  return [];
}

/** "1:42" for the seconds left on an offer. */
export const clock = (ms: number): string => { const s = Math.max(0, Math.ceil(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };

/** Ticks every second while `until` is ahead; returns the time now. */
export function useTicking(until: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (until === null || Date.now() >= until) return;
    const id = setInterval(() => { const t = Date.now(); setNow(t); if (t >= until) clearInterval(id); }, 1000);
    return () => clearInterval(id);
  }, [until]);
  return now;
}
